<template>
  <div class="pb-10">
    <ProfileShopHeader />

    <ProfileShopBanner :banners="props.banners" />

    <CompareStyleToolbar
      v-if="hasCompareStyleImages"
      :selected-image="selectedCompareStyleImage"
      :original-images="filters.original_preview_images"
      @update:selected-image="handleCompareStyleSelection"
    />

    <FilterToolbar @open-filter-panel="isFilterPanelOpen = true" />

    <ProfileGrid
      v-if="profilesQueryData.profiles.length"
      class="mt-8"
      :profiles="profilesQueryData.profiles"
      :is-loading="isProgressAnimationActive || isProfilesQueryLoading || isProfilesQueryFetching"
      :skeleton-count="profilesQueryData.profiles.length"
      :selected-compare-style-image="selectedCompareStyleImage"
      source="main_page"
    />

    <NoResults v-else class="mt-8" @reset="resetFilters(FilterResetSource.EmptyState)" />
  </div>

  <FilterPanel
    :is-open="isFilterPanelOpen"
    :label="$t('pages.profile-shop.filters.apply', { count: profilesQueryData.total_count })"
    :is-loading="isProfilesQueryFetching"
    @close="isFilterPanelOpen = false"
  />
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import ProfileShopBanner from "@/features/profile-shop/components/mainPage/ProfileShopBanner.vue";
import FilterPanel from "@/features/profile-shop/components/filters/FilterPanel.vue";
import FilterToolbar from "@/features/profile-shop/components/filters/toolbar/FilterToolbar.vue";
import CompareStyleToolbar from "@/features/profile-shop/components/compareStyle/CompareStyleToolbar.vue";
import {
  CompareStyleImages,
  IProfileShopFiltersResponse,
  IProfileShopProfilesResponse,
  ProfileShopBanner as ProfileShopBannerType,
} from "@/features/profile-shop/services/api";
import {
  FilterResetSource,
  ProfileShopFiltersState,
  provideProfileShopFilters,
} from "@/features/profile-shop/composables/useProfileShopFilters";

import NoResults from "@/features/profile-shop/components/filters/NoResults.vue";
import ProfileGrid from "@/features/profile-shop/components/profileGrid/ProfileGrid.vue";
import ProfileShopHeader from "@/features/profile-shop/components/mainPage/ProfileShopHeader.vue";
import { funnelService } from "@/services/funnelService";

const props = defineProps<{
  initialProfiles: IProfileShopProfilesResponse;
  filters: IProfileShopFiltersResponse;
  initialFilters: ProfileShopFiltersState;
  banners: ProfileShopBannerType[];
}>();

const isFilterPanelOpen = ref(false);
const {
  isProfilesQueryLoading,
  isProfilesQueryFetching,
  profilesQueryData,
  resetFilters,
  isProgressAnimationActive,
  startProgressAnimation,
} = provideProfileShopFilters(props.filters, props.initialProfiles, props.initialFilters);

const selectedCompareStyleImage = ref<keyof CompareStyleImages>("original");
const hasCompareStyleImages = computed(() => {
  return Object.keys(props.filters.original_preview_images).length > 0;
});

function handleCompareStyleSelection(imageKey: keyof CompareStyleImages) {
  selectedCompareStyleImage.value = imageKey;
  funnelService.trackEvent("profile_shop_compare_style_photo_selected", {
    photo_selected: imageKey,
  });
  startProgressAnimation();
}
</script>

<style>
html {
  font-size: 16px !important;
}
</style>
